/**
 * itemBench — the pure, headless brain behind TrainingScene's "Item Bench".
 *
 * The bench gives the player an UNLIMITED supply of the MVP trainer-item families
 * (a Rare Candy + the six vitamins + the evolution stones) to lavish on their team —
 * pure pressure-free power growth, never a "wasted my only one" anxiety (CLAUDE.md
 * pillar 6). This module owns the catalog (grouped for the picker) + the message
 * wording + the apply-and-auto-learn glue, all as pure functions over a
 * {@link PokemonInstance}, so the wording, the move-learning and the stone-evolution
 * resolution are unit-testable without a Phaser/DOM scene. The scene only renders
 * menus + a DialogBox and calls {@link applyBenchItem}; it never owns the rules.
 *
 * Items mutate the instance in place via core/items (the same convention battle XP uses);
 * the scene persists afterwards via the GameSession. A STONE is the exception — it never
 * mutates: it asks core/evolution whether it applies and, if so, returns a NEW evolved
 * instance (a {@link BenchEvolution}) the caller swaps in after the same calm
 * Evolve/Keep choice the Rare-Candy path uses.
 */
import type { PokemonInstance, SpeciesData, MoveData, StatKey } from '@/shared';
import { applyRareCandy, applyVitamin, VITAMINS, EVOLUTION_STONES } from '@/core/items.js';
import { checkEvolution, evolveInstance } from '@/core/evolution.js';

/** One pickable bench item — the Rare Candy, a vitamin, or an evolution stone. */
export interface BenchItem {
  /** Stable id ('rare-candy' | a vitamin itemId | a stone itemId). */
  readonly id: string;
  /** Menu label (e.g. 'Rare Candy', 'Protein', 'Water Stone'). */
  readonly label: string;
  /** A short one-line description shown under the picker. */
  readonly blurb: string;
  /** 'candy' levels the mon; 'vitamin' adds an EV; 'stone' evolves (if it applies). */
  readonly kind: 'candy' | 'vitamin' | 'stone';
  /** For a vitamin: which stat it trains (absent otherwise). */
  readonly stat?: StatKey;
}

/** A labelled run of bench items, for the grouped picker (e.g. a "Stones" section). */
export interface BenchGroup {
  /** Section heading shown as a non-selectable divider row. */
  readonly title: string;
  /** The items under this heading, in display order. */
  readonly items: readonly BenchItem[];
}

/** Friendly stat names for the vitamin confirmation lines. */
const STAT_LABEL: Readonly<Record<StatKey, string>> = {
  hp: 'HP',
  atk: 'Attack',
  def: 'Defense',
  spatk: 'Sp. Atk',
  spdef: 'Sp. Def',
  spe: 'Speed',
};

/** Per-vitamin cozy blurbs, keyed by stat. */
const VITAMIN_BLURB: Readonly<Record<StatKey, string>> = {
  hp: 'A nutritious drink that raises HP.',
  atk: 'A protein drink that raises Attack.',
  def: 'A mineral tonic that raises Defense.',
  spatk: 'A bracing drink that raises Sp. Atk.',
  spdef: 'A soothing drink that raises Sp. Def.',
  spe: 'A zesty drink that raises Speed.',
};

/**
 * Per-stone cozy blurbs, keyed by itemId. Any stone not listed falls back to a
 * generic line (keeps the bench working if the dataset adds a new stone).
 */
const STONE_BLURB: Readonly<Record<string, string>> = {
  'fire-stone': 'A fiery stone — some Pokémon evolve when touched by its heat.',
  'water-stone': 'A clear blue stone — some Pokémon evolve in its cool light.',
  'thunder-stone': 'A crackling stone — some Pokémon evolve at its spark.',
  'leaf-stone': 'A leafy green stone — some Pokémon evolve in its forest hush.',
  'moon-stone': 'A pale stone — some Pokémon evolve under its moonlit glow.',
  'sun-stone': 'A warm stone — some Pokémon evolve in its sunny radiance.',
  'ice-stone': 'A frosty stone — some Pokémon evolve in its wintry chill.',
  'shiny-stone': 'A stone that shines — some Pokémon evolve in its gentle gleam.',
  'dusk-stone': 'A shadowy stone — some Pokémon evolve in its dim dusk-light.',
  'dawn-stone': 'A stone like the dawn sky — some Pokémon evolve in its first light.',
  'black-augurite': 'A dark, glassy ore — some Pokémon evolve when it is held close.',
  'galarica-cuff': 'A woven cuff of Galarica twigs — a certain Pokémon evolves with it.',
  'galarica-wreath': 'A regal wreath of Galarica twigs — a certain Pokémon evolves with it.',
  'peat-block': 'A block of rich peat — a certain Pokémon evolves under a full moon’s peat.',
};

/** A friendly Title-Case label for a stone itemId (e.g. 'water-stone' → 'Water Stone'). */
function stoneLabel(itemId: string): string {
  return itemId
    .split('-')
    .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/**
 * The Rare Candy + the six vitamins, in canonical order. Kept as the historical
 * "trainer-item" catalog (the stones are a separate group — see {@link STONE_ITEMS}
 * and {@link BENCH_GROUPS}). Every item is unlimited — no quantity is ever shown.
 */
export const BENCH_ITEMS: readonly BenchItem[] = [
  {
    id: 'rare-candy',
    label: 'Rare Candy',
    blurb: 'A sweet treat that instantly raises a Pokémon by one level.',
    kind: 'candy',
  },
  ...VITAMINS.map(
    (v): BenchItem => ({
      id: v.itemId,
      label: v.label,
      blurb: VITAMIN_BLURB[v.stat],
      kind: 'vitamin',
      stat: v.stat,
    }),
  ),
];

/**
 * The evolution stones, derived from the dataset's item-evolution itemIds
 * ({@link EVOLUTION_STONES}) so the bench stays in lock-step with the data. Every
 * stone is unlimited; using one on a mon that it doesn't apply to is a harmless
 * "no effect" (see {@link applyBenchItem}).
 */
export const STONE_ITEMS: readonly BenchItem[] = EVOLUTION_STONES.map(
  (itemId): BenchItem => ({
    id: itemId,
    label: stoneLabel(itemId),
    blurb: STONE_BLURB[itemId] ?? 'An evolution stone — some Pokémon evolve when it is used.',
    kind: 'stone',
  }),
);

/**
 * The full picker catalog as labelled SECTIONS (Rare Candy + vitamins under "Treats",
 * the stones under "Stones"). The scene renders each group's title as a calm,
 * non-selectable divider row and the items beneath it.
 */
export const BENCH_GROUPS: readonly BenchGroup[] = [
  { title: 'Treats', items: BENCH_ITEMS },
  { title: 'Stones', items: STONE_ITEMS },
];

/** Every pickable item, flattened across groups (handy for lookups/tests). */
export const ALL_BENCH_ITEMS: readonly BenchItem[] = BENCH_GROUPS.flatMap((g) => g.items);

/**
 * Evolution info returned when a Rare Candy pushes a mon across its evolution
 * threshold. {@link evolveInstance} returns a NEW instance, so the caller must SWAP
 * `evolvedInstance` into the team (the in-place mutation only covered the level-up).
 */
export interface BenchEvolution {
  /** The species the mon evolved FROM (its species id before the candy). */
  readonly fromSpeciesId: string;
  /** The species the mon evolved INTO — for the flourish + the team's new species. */
  readonly toSpecies: SpeciesData;
  /** The NEW evolved instance (stats recomputed); swap this into the team in place of `mon`. */
  readonly evolvedInstance: PokemonInstance;
}

/** The result of applying a bench item — what the scene shows + whether to re-render. */
export interface BenchApplyResult {
  /** The celebratory / informative line(s) for the DialogBox (≥1 page). */
  readonly pages: readonly string[];
  /** True if the mon's level/stats/moves changed (the party menu should refresh). */
  readonly changed: boolean;
  /**
   * Present (and `evolved` is implied) when a Rare Candy triggered an evolution. The
   * level-up was applied in place to `mon`; the EVOLVED form is a NEW instance the caller
   * must swap into the team. Absent when nothing evolved.
   */
  readonly evolution?: BenchEvolution;
}

/** Lookup of move id → data, for auto-learning Rare-Candy moves into empty slots. */
export type MoveLookup = (id: string) => MoveData | undefined;

/**
 * Lookup of species id → data, for resolving an evolution target after a Rare Candy.
 * Passed in (not imported) so itemBench stays pure + testable — the scene wires
 * `(id) => this.index.speciesById(id)`. Optional: when absent, evolutions are skipped
 * (the candy still levels), keeping the old call sites working.
 */
export type SpeciesLookup = (id: string) => SpeciesData | undefined;

/**
 * Apply a bench item to `mon` (mutating it in place via core/items), auto-learning any
 * newly-learnable move into an EMPTY move slot (never overwriting — no pressure), and
 * return the juicy confirmation pages + a `changed` flag.
 *
 * Rare Candy → "<Name> grew to Lv N!" (+ "<Name> learned <Move>!" per auto-learned move,
 * or a gentle note that a move is ready to learn when all four slots are full). At
 * LEVEL_MAX it's a warm "<Name> is already at the peak — Lv 100!" with no mutation. When
 * the new level meets a level-up evolution's minLevel, the result carries `evolution`
 * (a NEW evolved instance the caller swaps into the team — the UI presents the flourish).
 * Vitamin → "<Name>'s <Stat> grew!" or "<Name>'s <Stat> won't go any higher!" when capped.
 *
 * `getSpecies` resolves an evolution target species; when omitted, evolutions are skipped
 * (the candy still levels) so existing two-/three-arg call sites keep working.
 */
export function applyBenchItem(
  item: BenchItem,
  mon: PokemonInstance,
  species: SpeciesData,
  getMove: MoveLookup,
  getSpecies?: SpeciesLookup,
): BenchApplyResult {
  const name = displayName(mon, species);
  if (item.kind === 'candy') return applyCandy(name, mon, species, getMove, getSpecies);
  if (item.kind === 'stone') return applyStone(item, name, mon, species, getSpecies);
  return applyVit(item, name, mon, species);
}

/**
 * Use an evolution STONE on `mon`. Asks core/evolution whether this stone applies
 * (`checkEvolution(mon, species, 'item', { itemId })`); if it matches and the target
 * species resolves, returns the SAME {@link BenchEvolution} shape the Rare-Candy path
 * returns — so TrainingScene plays the existing flourish + Evolve/Keep choice + team
 * swap verbatim. A stone NEVER mutates the mon (no level/EV change); it only proposes
 * the evolved instance, which the caller swaps in only if the player confirms.
 *
 * If the stone doesn't apply to this Pokémon (wrong species, or no species lookup),
 * it's a calm no-effect: `changed:false`, no evolution, "It had no effect on <Name>."
 */
function applyStone(
  item: BenchItem,
  name: string,
  mon: PokemonInstance,
  species: SpeciesData,
  getSpecies: SpeciesLookup | undefined,
): BenchApplyResult {
  const noEffect: BenchApplyResult = {
    pages: [`It had no effect on ${name}.`],
    changed: false,
  };
  if (!getSpecies) return noEffect;

  const match = checkEvolution(mon, species, 'item', { itemId: item.id });
  if (!match) return noEffect;
  const toSpecies = getSpecies(match.toSpeciesId);
  if (!toSpecies) return noEffect;

  const evolvedInstance = evolveInstance(mon, toSpecies);
  // Mirror the candy path's final page so playBenchEvolution's "lift the last line"
  // framing works identically for a stone evolution.
  const pages = [`${name} evolved into ${toSpecies.name}!`];
  return {
    pages,
    changed: true,
    evolution: { fromSpeciesId: species.id, toSpecies, evolvedInstance },
  };
}

/** The mon's display name: its nickname, else the species name, else the raw id. */
export function displayName(mon: PokemonInstance, species: SpeciesData | undefined): string {
  return mon.nickname ?? species?.name ?? mon.speciesId;
}

function applyCandy(
  name: string,
  mon: PokemonInstance,
  species: SpeciesData,
  getMove: MoveLookup,
  getSpecies?: SpeciesLookup,
): BenchApplyResult {
  const result = applyRareCandy(mon, species);
  if (!result.leveled) {
    return {
      pages: [`${name} is already at the peak — Lv ${result.newLevel}!`],
      changed: false,
    };
  }

  const pages: string[] = [`${name} grew to Lv ${result.newLevel}!`];
  // UNLIMITED MOVES: each newly-learnable move is simply ADDED (full PP, uses:0), deduped
  // — no 4-move cap, no replace prompt (mirrors the battle level-up path).
  for (const moveId of result.learnableMoves) {
    if (mon.moves.some((m) => m.moveId === moveId)) continue;
    const move = getMove(moveId);
    if (!move) continue;
    mon.moves.push({ moveId, pp: move.pp, maxPp: move.pp, uses: 0 });
    pages.push(`${name} learned ${move.name ?? moveId}!`);
  }

  // Close the bench gap: a candy that crosses a level-up evolution's minLevel should
  // evolve, exactly like the post-battle pipeline (BattleScene.runLevelingPipeline).
  // We re-check the CURRENT species each candy, so a chain (Bulbasaur→Ivysaur at Lv16,
  // then later Ivysaur→Venusaur at Lv32) resolves one stage per qualifying candy.
  const evolution = maybeEvolve(name, mon, species, getSpecies, pages);
  return { pages, changed: true, ...(evolution ? { evolution } : {}) };
}

/**
 * After a Rare-Candy level-up, see if the mon now qualifies for a level-up evolution.
 * Mirrors BattleScene: checkEvolution → resolve the target SpeciesData → evolveInstance
 * (a NEW instance). Returns the evolution info (for the caller to swap in) or undefined.
 * Appends a celebratory line so a non-presenting caller still has wording. A mon below
 * its evolution's minLevel is rejected by checkEvolution and stays put.
 */
function maybeEvolve(
  name: string,
  mon: PokemonInstance,
  species: SpeciesData,
  getSpecies: SpeciesLookup | undefined,
  pages: string[],
): BenchEvolution | undefined {
  if (!getSpecies) return undefined;
  const match = checkEvolution(mon, species, 'level-up');
  if (!match) return undefined;
  const toSpecies = getSpecies(match.toSpeciesId);
  if (!toSpecies) return undefined;

  const evolvedInstance = evolveInstance(mon, toSpecies);
  pages.push(`${name} evolved into ${toSpecies.name}!`);
  return { fromSpeciesId: species.id, toSpecies, evolvedInstance };
}

function applyVit(
  item: BenchItem,
  name: string,
  mon: PokemonInstance,
  species: SpeciesData,
): BenchApplyResult {
  const stat = item.stat!;
  const statLabel = STAT_LABEL[stat];
  const result = applyVitamin(mon, stat, species);
  if (result.added === 0) {
    return { pages: [`${name}'s ${statLabel} won't go any higher!`], changed: false };
  }
  const tail = result.capped ? ' (it can take no more)' : '';
  return { pages: [`${name}'s ${statLabel} grew!${tail}`], changed: true };
}
